import { fetchCurrencies } from 'queries/currency/currency';
import React from 'react';
import {
  createContext,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { Dayjs } from 'dayjs';
import getDateRange from 'utils/dateRange/getDateRage';
import { fetchSales } from 'queries/sales/sales';

interface CurrencyContextType {
  currencies: { key: number; label: string; value: number }[] | undefined;
  loadCurrency: boolean;
  defaultCurrency: number | undefined;
  selectedCurrency: number | undefined;
  onSelectCurrency: (value: SetStateAction<number>) => void;
  onRangeChange: any;
  updatedSales: any;
  currency: string;
  allCurrencies: any;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(
  undefined
);

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const { currencies, isLoading } = fetchCurrencies();
  const [selectedCurrency, setSelectedCurrency] = useState<number | undefined>(
    undefined
  );

  const formatCurrencyList = currencies?.map(
    (
      currency: {
        currency_code: string;
        shop_currency_id: number;
        is_current: boolean;
      },
      index: number
    ) => ({
      key: index,
      label: currency?.currency_code,
      value: currency?.shop_currency_id,
      isDefault: currency?.is_current,
    })
  );

  const { mutate: requestSalesList, sales } = fetchSales();

  const defaultCurrency = currencies?.find(
    (item: { is_current: boolean }) => item?.is_current === true
  )?.shop_currency_id;

  const onSelectCurrency = (value: any) => {
    setSelectedCurrency(value);
  };

  const currency = currencies?.find(
    (item: { shop_currency_id: number | undefined }) =>
      item.shop_currency_id === selectedCurrency
  );

  useEffect(() => {
    if (!selectedCurrency && currencies?.length > 0) {
      const setCurrentCurrency =
        formatCurrencyList.find((item: any) => item?.isDefault)?.value ||
        formatCurrencyList?.[0]?.value;
      setSelectedCurrency(setCurrentCurrency || defaultCurrency);
    }
  }, [defaultCurrency, formatCurrencyList, selectedCurrency, currencies]);

  const onRangeChange = (
    dates: null | (Dayjs | null)[],
    _dateStrings: string[]
  ) => {
    if (dates) {
      const fromDate = getDateRange.formatDayjsDate(dates[0]);
      const toDate = getDateRange.formatDayjsDate(dates[1]);
      requestSalesList({
        currency_id: selectedCurrency,
        started_date: fromDate,
        ended_date: toDate,
      });
    }
  };

  return (
    <CurrencyContext.Provider
      value={{
        currencies: formatCurrencyList,
        loadCurrency: isLoading,
        defaultCurrency,
        selectedCurrency,
        onSelectCurrency,
        onRangeChange,
        updatedSales: sales,
        currency: currency?.currency_code,
        allCurrencies: currencies,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = React.useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
